import { DataHolderComponent, DisplayedComponent, isFocusableComponent, isValueComponent } from "components/components/Component";
import * as message from "components/message/message";
import { Renderer } from "components/Renderer";
import $ from "jquery";
import { ConfirmationDialogDefinition } from "../confirmationDialog/confirmationDialogComponent";
import { Action, isServerActionDefinition } from "./actions";
import { BackEvent } from "./backEvent";
import { Cursor } from "./cursor";
import { downloadFile, manageAttachments } from "./downloader";
import { Response } from "./response";
import { ServerActionRequest } from "./serverActionRequest";

export class DefaultResponseAction {
    private action: Action;

    constructor(action: Action) {
        this.action = action;
    }

    public static getCallBack(action: Action) {
        const responseAction = new DefaultResponseAction(action);
        return responseAction.defaultResponseAction;
    }

    private defaultResponseAction = (event: JQuery.Event, action: Action, response: Response | Buffer, jqXHR): any => {
        Cursor.end();
        ServerActionRequest.clearFisnished(action.definition);
        this.fireFinishedActionEvent();
        const ct: string = jqXHR.getResponseHeader("content-type") || "";
        if (!ct.startsWith("application/json")) {
            if (jqXHR.status === 200) {
                // If it is not a json but the response status is ok we assume it is a file to be downloaded.
                downloadFile(response as Buffer, jqXHR, ct);
                return;
            }
        }
        response = response as Response;
        if (response.componentType == null) {
            message.show({ key: "Unknown response type", level: "error" });
            console.error("Unknown response type");
            console.warn(response);
        } else if (response.componentType === "actionResponse") {
            if (isServerActionDefinition(action.definition) && action.definition.onBeforeDefaultResponse != null) {
                action.definition.onBeforeDefaultResponse(response);
            }
            $.each(response.messages, (key, val) => {
                message.show(val);
            });

            if (response.submitStatus === "committed" && this.action.dataHolder != null) {
                this.action.dataHolder.setCommittedStatus();
            }

            if (isServerActionDefinition(action.definition)) {
                if (action.definition.successUpdatesParent && response.submitStatus === "committed") {
                    $(action.dataHolder).trigger("fireParentRefreshNeeded");
                }
            }
            if (this.isConfimationDialog(response)) {
                response.bundle.data["x-accept-validation-override"] = true;
                (response.bundle.definition as ConfirmationDialogDefinition).data = response.bundle.data;
                (response.bundle.definition as ConfirmationDialogDefinition).action = this.action;
                (response.bundle.definition as ConfirmationDialogDefinition).component = this.action.dataHolder;
            }
            if (response.type === "back") {
                BackEvent.fire(action, response);
                if (response.targetComponent != null) {
                    this.manageUpdateResponse(response);
                }
            } else if (response.type === "update") {
                this.manageUpdateResponse(response);
            } else if (response.bundle != null && response.bundle.definition != null) {
                if (response.type === "replace") {
                    this.replaceComponentOnResponse(response);
                } else {
                    if (response.bundle.definition.componentType === "stackedPage") {
                        const dispatchView = this.getAView();
                        dispatchView.dispatchEvent(new CustomEvent(
                            "stackPageActionFired",
                            {
                                bubbles: true,
                                detail: { action: this.action },
                            }));
                    }
                    Renderer.instance().fullRender(response.bundle, null, this.action.dataHolder);
                }
            }
            if (this.action.done != null) {
                this.action.done(response);
            }
            if (response.actions?.length > 0) {
                for (const actionDefinition of response.actions) {
                    if (response.targetComponent != null) {

                        const e = this.findTargetElement(response.targetComponent);
                        e.dispatchEvent(new CustomEvent(
                            "componentActionFireRequested",
                            {
                                detail: {
                                    target: response.targetComponent,
                                    data: response.bundle.data,
                                    action: actionDefinition,
                                },
                            }),
                        );
                    } else if (response.type !== "back") {
                        new Action(actionDefinition, action.sourceComponent).run(response.bundle?.data);
                    }
                }
            }
        }
        manageAttachments(response);
    }

    private getAView() {
        return this.action.dataHolder?.["view"][0] ?? this.action.dataHolder?.["view"] ?? this.action.sourceComponent["view"];
    }

    private fireFinishedActionEvent() {
        document.dispatchEvent(new CustomEvent(
            "sislogActionEvent",
            {
                bubbles: true,
                detail: {
                    phase: "finish",
                    action: this.action,
                },
            }));
    }

    private replaceComponentOnResponse(response: Response) {
        if (response.targetComponent == null) {
            Renderer.instance().fullBundleReplace(
                response.bundle,
                (this.action.dataHolder as unknown) as DisplayedComponent);
        } else {
            // We need to find the view for the target.
            // We do not keep a reference of the rendered components.
            // We need to find the component view in the DOM.
            const e = this.findTargetElement(response.targetComponent);
            if (e == null) {
                console.error("Target component not found ", response.targetComponent);
            } else {
                Renderer.instance().fullBundleReplace(
                    response.bundle,
                    e);
            }
        }
    }

    private manageUpdateResponse(response: Response) {
        if (response.targetComponent == null) {
            if (this.action.dataHolder != null) {
                this.manageUpdateResponseOnDataHolder(response);
            } else if (this.isUpdatesData(this.action.sourceComponent)) {
                this.action.sourceComponent.updateData(response.bundle.data);
            }
        } else {
            this.manageUpdateResponseOnTarget(response);
        }
    }

    private manageUpdateResponseOnDataHolder(response: Response) {
        if (response.bundle == null) {
            // This is only used for the table to autoreload data.
            this.action.dataHolder.updateData(null);
        } else {
            if (response.bundle.options != null) {
                Renderer.instance().iterateToSetOptions(
                    this.action.dataHolder,
                    response.bundle.options);
            }
            if (response.bundle.restrictions != null) {
                Renderer.instance().iterateToSetRestrictions(
                    this.action.dataHolder,
                    response.bundle.restrictions);
            }
            Renderer.instance().iterateToUpdateData(
                this.action.dataHolder,
                response.bundle.data ?? {});
            if (response.bundle.focus != null) {
                Renderer.instance().iterateToSetFocus(this.action.dataHolder, response.bundle.focus);
            }
        }
    }

    private manageUpdateResponseOnTarget(response: Response) {
        const e = this.findTargetElement(response.targetComponent);
        if (e != null) {
            if (response.submitStatus === "committed") {
                e.dispatchEvent(new CustomEvent(
                    "clearDataCommitRequested",
                    {
                        detail: {
                            target: response.targetComponent,
                        },
                    }),
                );
            }
            e.dispatchEvent(new CustomEvent(
                "componentDataUpdateRequested",
                {
                    detail: {
                        target: response.targetComponent,
                        data: response.bundle.data,
                        restrictions: response.bundle.restrictions,
                        options: response.bundle.options,
                    },
                }),
            );
            if (response.bundle.focus != null) {
                e.dispatchEvent(new CustomEvent(
                    "componentFocusRequested",
                    {
                        detail: {
                            target: response.targetComponent,
                            focus: response.bundle.focus,
                        },
                    }),
                );
            }
        } else {
            this.action.triggerEvent(
                "componentDataUpdateRequested",
                {
                    target: response.targetComponent,
                    data: response.bundle.data,
                    restrictions: response.bundle.restrictions,
                    options: response.bundle.options,
                },
            );
        }
    }

    private findTargetElement(targetId: string): Element {
        return document.querySelector("[data-s6-id='" + targetId + "']");
    }

    private isUpdatesData(arg: any): arg is DataHolderComponent {
        return arg.updateData !== undefined;
    }

    private isConfimationDialog(response: Response): boolean {
        return (response.bundle != null
            && response.bundle.definition != null
            && response.bundle.definition.componentType === "confirmationDialog");
    }
}
